"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  MoreHorizontal,
  Play,
  Pause,
  Clock,
  SkipBack,
  SkipForward,
  ChevronDown,
  Shuffle,
  Repeat,
  Share2,
  MonitorSpeaker,
  Plus,
  Mic2,
} from "lucide-react";

// ─── Paleta — tons usados pelo wizard ────────────────────────────────────────

const THEME_PALETTE: Record<
  string,
  {
    from: string; // cor "quente" no topo (acima)
    to: string;   // cor "complementar" do gradiente
    bg: string;   // fundo do app (Spotify usa #121212; usamos o mesmo padrão)
    accent: string;
    accentRgb: string;
  }
> = {
  "purple-romantic": {
    from: "#5B21B6",
    to: "#EC4899",
    bg: "#121212",
    accent: "#C4B5FD",
    accentRgb: "196,181,253",
  },
  "midnight-rose": {
    from: "#1E0B3A",
    to: "#BE185D",
    bg: "#0A0415",
    accent: "#F472B6",
    accentRgb: "244,114,182",
  },
  "deep-wine": {
    from: "#3B0764",
    to: "#881337",
    bg: "#0D0009",
    accent: "#E879A0",
    accentRgb: "232,121,160",
  },
  "ocean-love": {
    from: "#0C4A6E",
    to: "#7C3AED",
    bg: "#020C16",
    accent: "#7DD3FC",
    accentRgb: "125,211,252",
  },
  "sweet-pink": {
    from: "#DB2777",
    to: "#FBA8C7",
    bg: "#1F050E",
    accent: "#FCE7F3",
    accentRgb: "252,231,243",
  },
  // Tema custom — usuário subiu uma imagem própria. Paleta neutra-escura
  // (cor real vem da imagem blurada no background). Accent rosa pra
  // bater com a identidade do app.
  "custom-photo": {
    from: "#1F2937",
    to: "#1F2937",
    bg: "#0A0A0F",
    accent: "#E11D48",
    accentRgb: "225,29,72",
  },
  "custom-photo-sharp": {
    from: "#1F2937",
    to: "#1F2937",
    bg: "#0A0A0F",
    accent: "#E11D48",
    accentRgb: "225,29,72",
  },
};
const DEFAULT_PALETTE = THEME_PALETTE["purple-romantic"];

export function getSpotifyPalette(templateSlug?: string) {
  return THEME_PALETTE[templateSlug ?? ""] ?? DEFAULT_PALETTE;
}

// ─── Contador ao vivo ────────────────────────────────────────────────────────

function useLiveCounter(startDate: string | undefined) {
  const [elapsed, setElapsed] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  useEffect(() => {
    if (!startDate) return;
    const update = () => {
      const diff = Date.now() - new Date(startDate).getTime();
      if (diff <= 0) return;
      const s = Math.floor(diff / 1000);
      setElapsed({
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
      });
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [startDate]);
  return elapsed;
}

// ─── Dados ───────────────────────────────────────────────────────────────────

export interface SpotifyLovePageData {
  partnerName?: string;
  recipientName?: string;
  title?: string;
  coupleStartDate?: string;
  loveLetter?: string;
  musicName?: string;
  artistName?: string;
  musicUrl?: string;
  musicPreviewUrl?: string;
  photoUrls: string[];
  templateSlug?: string;
  /** Imagem custom escolhida no StepTheme — vai pro background do
   *  template (blurada ou nítida conforme o slug). Não é usada como
   *  capa central do "álbum" — essa é a artwork da música. */
  coverPhotoUrl?: string | null;
  /** Capa da música (vinda do Spotify/iTunes search). Mantida na
   *  interface por retrocompat com outros lugares que ainda passam, mas
   *  NÃO é usada como capa central — o quadrado mostra exclusivamente
   *  a primeira foto do StepPhotos (ou placeholder de coração). */
  musicArtworkUrl?: string | null;
  // Conteúdo opcional renderizado logo abaixo do player (acima de "Up next").
  extraSections?: React.ReactNode;
}

// ─── Página estilo Spotify ───────────────────────────────────────────────────

export function SpotifyLovePage({
  data,
  disableMusicAutoplay = false,
}: {
  data: SpotifyLovePageData;
  // Suprime o play automático no mount. Usado em previews internos do
  // wizard (mockup lateral, modal "Ver como vai ficar") pra evitar várias
  // instâncias da mesma música tocando ao mesmo tempo. A página publicada
  // real (/p/<slug>) NÃO passa esse flag, mantendo o autoplay.
  disableMusicAutoplay?: boolean;
}) {
  const counter = useLiveCounter(data.coupleStartDate);
  const [playing, setPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(180);
  const [liked, setLiked] = useState(false);
  const [added, setAdded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const palette = getSpotifyPalette(data.templateSlug);

  useEffect(() => {
    const previewUrl = data.musicPreviewUrl;
    if (!previewUrl) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlaying(false);
      setAudioProgress(0);
      return;
    }
    const audio = new Audio(previewUrl);
    audio.volume = 0.65;
    // Loop nativo do <audio>: ao chegar no fim, o browser reinicia do 0
    // automaticamente sem pausar. Evita gap de áudio entre os ciclos.
    audio.loop = true;
    audioRef.current = audio;
    setAudioProgress(0);
    audio.addEventListener("loadedmetadata", () =>
      setAudioDuration(audio.duration || 30)
    );
    audio.addEventListener("timeupdate", () => {
      if (audio.duration)
        setAudioProgress((audio.currentTime / audio.duration) * 100);
    });
    // Fallback: alguns browsers/iOS podem não disparar o loop=true em
    // certos contextos. Quando 'ended' disparar, reinicia manual.
    audio.addEventListener("ended", () => {
      audio.currentTime = 0;
      audio.play().catch(() => setPlaying(false));
    });
    if (!disableMusicAutoplay) {
      audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
    return () => {
      audio.pause();
      audio.src = "";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.musicPreviewUrl, disableMusicAutoplay]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [playing]);

  // Duas imagens INDEPENDENTES:
  // 1. backgroundImageUrl — entra fixo no fundo (sticky 100vh). Só vem
  //    do tema custom (coverPhotoUrl do StepTheme). Foto da galeria
  //    nunca é usada aqui — senão fica idêntica à capa central.
  // 2. coverUrl — quadrado central tipo "capa do álbum". É EXCLUSIVO
  //    pra primeira foto que o usuário escolheu no StepPhotos. NUNCA
  //    cai pra artwork da música (cliente foi explícito: "a foto da
  //    música não é pra aparecer"). Quando não tem foto, mostra
  //    coração como placeholder em vez de qualquer outra imagem.
  // - "custom-photo" = bg blur ambiente (texto continua legível)
  // - "custom-photo-sharp" = bg sharp ocupando a tela, estilo pôster
  const isCustomThemeOpaque = data.templateSlug === "custom-photo";
  const isCustomThemeSharp = data.templateSlug === "custom-photo-sharp";
  const isCustomTheme = isCustomThemeOpaque || isCustomThemeSharp;
  const backgroundImageUrl = isCustomTheme ? data.coverPhotoUrl ?? null : null;
  const coverUrl = data.photoUrls[0] || null;
  const coupleName =
    [data.partnerName, data.recipientName].filter(Boolean).join(" & ") ||
    "Você & Alguém Especial";
  const title = data.title || "Nossa ILovePage";
  // Decisão de design: a faixa "principal" da capa mostra SEMPRE o nome do
  // casal — protagonismo pro presente. O nome real da música aparece em
  // outro lugar (top bar abaixo de "Tocando •"), bem ao estilo Spotify.
  const songName = coupleName;
  const artistName =
    data.title && data.title !== coupleName ? data.title : "ILovePage";

  // Texto da top bar — quando tem música, lembra qual é (formato
  // "Música — Artista"); quando não tem, mostra o título da página.
  const hasMusic = Boolean(data.musicName);
  const topBarLabel = hasMusic ? "Tocando •" : "Tocando do álbum";
  const topBarTitle = hasMusic
    ? data.artistName
      ? `${data.musicName} — ${data.artistName}`
      : data.musicName!
    : title;
  const hasPreview = !!data.musicPreviewUrl;
  const hasCounter = !!data.coupleStartDate;
  const hasLetter = !!data.loveLetter;
  const fmtDur = (s: number) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const elapsed = (audioProgress / 100) * audioDuration;

  return (
    <div
      className="w-full relative"
      style={{
        background: palette.bg,
        fontFamily:
          'CircularStd, "Spotify Circular", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Background do tema custom — FIXO na tela enquanto o conteúdo
          scrolla por cima. Fica como filho direto do wrapper externo
          (NÃO dentro do container max-w-440 do "celular") porque o
          overflow-x-hidden daquele div torna ele o scroll container do
          sticky e prende o background lá. Aqui o scroll container é o
          parent natural (body na página publicada, ou o div com
          overflow-y-auto do CreateLivePreview no wizard).
          - opaca: blur forte + brilho reduzido (ambiente, texto legível)
          - nítida: imagem inteira, sharp, ocupa toda a tela como pôster */}
      {isCustomTheme && backgroundImageUrl ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none"
            style={{
              position: "sticky",
              top: 0,
              height: "100vh",
              marginBottom: "-100vh",
              zIndex: 0,
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              ...(isCustomThemeOpaque
                ? {
                    filter: "blur(40px) saturate(1.4) brightness(0.6)",
                    transform: "scale(1.15)",
                  }
                : {
                    filter: "saturate(1.05)",
                  }),
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none"
            style={{
              position: "sticky",
              top: 0,
              height: "100vh",
              marginBottom: "-100vh",
              zIndex: 0,
              background: isCustomThemeOpaque
                ? `linear-gradient(to bottom, ${palette.bg}66 0%, ${palette.bg}99 50%, ${palette.bg}dd 100%)`
                : `linear-gradient(to bottom, ${palette.bg}33 0%, ${palette.bg}77 60%, ${palette.bg}cc 100%)`,
            }}
          />
        </>
      ) : null}

      {/* Container "telefone" centrado — fica natural no desktop e no mobile.
          Sem altura fixa: o parent decide se rola (wizard) ou cresce (página).
          z-10 garante que o conteúdo passa POR CIMA do bg fixo acima. */}
      <div
        className="mx-auto relative overflow-x-hidden z-10"
        style={{ maxWidth: 440 }}
      >
        {/* Gradient de cor padrão — só renderiza quando NÃO tem tema
            custom. Quando tem custom, o background fixo lá em cima já
            cuida visual; mostrar esse gradient junto polui. */}
        {!isCustomTheme && (
          <div
            className="absolute inset-x-0 top-0 pointer-events-none"
            style={{
              height: 520,
              background: `
                radial-gradient(120% 80% at 50% 0%, ${palette.from} 0%, ${palette.from}cc 18%, ${palette.from}55 38%, ${palette.bg} 68%, ${palette.bg} 100%)
              `,
            }}
          />
        )}

        <div className="relative z-10 flex flex-col px-5 pt-3 pb-8">
          {/* ─── Top bar ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between pt-2 pb-4">
            <button
              type="button"
              aria-label="Voltar"
              className="h-8 w-8 -ml-1.5 flex items-center justify-center text-white"
            >
              <ChevronDown className="h-6 w-6" />
            </button>
            <div className="text-center min-w-0 px-2">
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.12em]"
                style={{ color: "rgba(255,255,255,0.7)" }}
              >
                {topBarLabel}
              </p>
              <p className="text-[13px] font-bold text-white leading-tight truncate max-w-[200px] mx-auto">
                {topBarTitle}
              </p>
            </div>
            <button
              type="button"
              aria-label="Mais opções"
              className="h-8 w-8 -mr-1.5 flex items-center justify-center text-white"
            >
              <MoreHorizontal className="h-6 w-6" />
            </button>
          </div>

          {/* ─── Album art ───────────────────────────────────────────────── */}
          <div className="flex justify-center pt-2 pb-6">
            <motion.div
              animate={playing ? { scale: 1 } : { scale: 0.93 }}
              transition={{ duration: 0.4, ease: [0.2, 0.9, 0.3, 1] }}
              className="w-full aspect-square overflow-hidden"
              style={{
                maxWidth: 340,
                borderRadius: 6,
                boxShadow: playing
                  ? `0 30px 60px rgba(0,0,0,0.7), 0 10px 22px rgba(0,0,0,0.45)`
                  : "0 24px 40px rgba(0,0,0,0.55)",
              }}
            >
              {coverUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={coverUrl}
                  alt="Capa"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
                  }}
                >
                  <Heart
                    className="h-20 w-20 text-white/70"
                    fill="rgba(255,255,255,0.7)"
                  />
                </div>
              )}
            </motion.div>
          </div>

          {/* ─── Faixa: título + artista + add ───────────────────────────── */}
          <div className="flex items-center justify-between mt-1 mb-3">
            <div className="min-w-0 flex-1 pr-3">
              <p
                className="text-white font-bold leading-snug truncate"
                style={{ fontSize: 22, letterSpacing: "-0.01em" }}
              >
                {songName}
              </p>
              <p
                className="truncate mt-1"
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 14,
                }}
              >
                {artistName}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAdded((v) => !v)}
              aria-label={added ? "Adicionado à biblioteca" : "Adicionar à biblioteca"}
              className="shrink-0 h-8 w-8 flex items-center justify-center"
            >
              {added ? (
                <span
                  className="h-7 w-7 rounded-full flex items-center justify-center"
                  style={{ background: "#1ED760", color: "#0F0F12" }}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                    <path
                      d="M5 12l5 5L20 7"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              ) : (
                <Plus
                  className="h-7 w-7"
                  strokeWidth={2}
                  style={{ color: "rgba(255,255,255,0.85)" }}
                />
              )}
            </button>
          </div>

          {/* ─── Barra de progresso ──────────────────────────────────────── */}
          <div className="mb-1 mt-2">
            <div
              className="relative h-1 rounded-full"
              style={{ background: "rgba(255,255,255,0.18)" }}
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: `${hasPreview ? audioProgress : 22}%`,
                  background: "rgba(255,255,255,0.95)",
                  transition: hasPreview ? "width 0.5s linear" : "none",
                }}
              />
              <div
                className="absolute top-1/2"
                style={{
                  left: `${hasPreview ? audioProgress : 22}%`,
                  transform: "translate(-50%, -50%)",
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: "white",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              />
            </div>
            <div
              className="flex justify-between mt-1.5"
              style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}
            >
              <span className="tabular-nums">
                {hasPreview ? fmtDur(elapsed) : "0:42"}
              </span>
              <span className="tabular-nums">
                -{hasPreview ? fmtDur(Math.max(audioDuration - elapsed, 0)) : "2:21"}
              </span>
            </div>
          </div>

          {/* ─── Controles de playback ───────────────────────────────────── */}
          <div className="flex items-center justify-between mt-3 mb-4">
            <button
              type="button"
              aria-label="Aleatório"
              className="h-10 w-10 flex items-center justify-center relative"
            >
              <Shuffle
                className="h-[18px] w-[18px]"
                style={{ color: "#1ED760" }}
              />
              <span
                className="absolute bottom-1 h-1 w-1 rounded-full"
                style={{ background: "#1ED760" }}
              />
            </button>
            <button
              type="button"
              aria-label="Anterior"
              className="h-10 w-10 flex items-center justify-center text-white"
            >
              <SkipBack className="h-7 w-7" fill="white" />
            </button>
            <button
              type="button"
              onClick={hasPreview ? togglePlay : undefined}
              aria-label={playing ? "Pausar" : "Tocar"}
              className="flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: 999,
                background: "white",
                boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
              }}
            >
              {playing ? (
                <Pause
                  className="h-7 w-7"
                  style={{ color: "#000" }}
                  fill="#000"
                />
              ) : (
                <Play
                  className="h-7 w-7 translate-x-[2px]"
                  style={{ color: "#000" }}
                  fill="#000"
                />
              )}
            </button>
            <button
              type="button"
              aria-label="Próxima"
              className="h-10 w-10 flex items-center justify-center text-white"
            >
              <SkipForward className="h-7 w-7" fill="white" />
            </button>
            <button
              type="button"
              aria-label="Repetir"
              className="h-10 w-10 flex items-center justify-center"
            >
              <Repeat
                className="h-[18px] w-[18px]"
                style={{ color: "rgba(255,255,255,0.6)" }}
              />
            </button>
          </div>

          {/* ─── Devices / Share row ─────────────────────────────────────── */}
          <div className="flex items-center justify-between pt-1 pb-2">
            <div className="flex items-center gap-2 min-w-0">
              <MonitorSpeaker
                className="h-4 w-4 shrink-0"
                style={{ color: "#1ED760" }}
              />
              <span
                className="text-[12px] font-semibold truncate"
                style={{ color: "#1ED760" }}
              >
                Este dispositivo
              </span>
            </div>
            <button
              type="button"
              onClick={() => setLiked((v) => !v)}
              aria-label="Compartilhar"
              className="h-9 w-9 flex items-center justify-center"
            >
              <Share2
                className="h-[18px] w-[18px]"
                style={{ color: "rgba(255,255,255,0.85)" }}
              />
            </button>
          </div>

          {/* ─── Contador (estilo card "Now Playing View") ───────────────── */}
          {hasCounter && (
            <div
              className="rounded-xl p-4 mt-4"
              style={{
                background: `linear-gradient(135deg, rgba(${palette.accentRgb},0.18) 0%, rgba(${palette.accentRgb},0.06) 100%)`,
                border: `1px solid rgba(${palette.accentRgb},0.18)`,
              }}
            >
              <div className="flex items-center gap-1.5 mb-3">
                <Clock
                  className="h-3.5 w-3.5"
                  style={{ color: palette.accent }}
                />
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: palette.accent }}
                >
                  Juntos há
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: counter.days, l: "dias" },
                  { v: counter.hours, l: "horas" },
                  { v: counter.minutes, l: "min" },
                  { v: counter.seconds, l: "seg" },
                ].map(({ v, l }) => (
                  <div
                    key={l}
                    className="rounded-lg flex flex-col items-center py-2.5"
                    style={{ background: "rgba(255,255,255,0.06)" }}
                  >
                    <motion.span
                      key={v}
                      initial={{ y: -4, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      className="text-white font-black tabular-nums"
                      style={{ fontSize: 18 }}
                    >
                      {String(v).padStart(2, "0")}
                    </motion.span>
                    <span
                      className="mt-0.5"
                      style={{
                        fontSize: 9,
                        color: "rgba(255,255,255,0.45)",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {l.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Lyrics card (Carta de amor) ─────────────────────────────── */}
          {hasLetter && (
            <div
              className="rounded-xl overflow-hidden mt-4 relative"
              style={{
                background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
                minHeight: 200,
              }}
            >
              <div className="p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p
                    className="text-[12px] font-bold uppercase tracking-[0.14em] text-white"
                  >
                    Letra
                  </p>
                  <Mic2 className="h-4 w-4 text-white/80" />
                </div>
                <div className="flex flex-col gap-1.5">
                  {(() => {
                    // Mostra até 8 linhas. Acima disso fica fora do card.
                    const lines = data
                      .loveLetter!.split(/\n+/)
                      .filter((l) => l.trim())
                      .slice(0, 8);
                    return lines.map((line, i) => {
                      // Opacidade decresce SUAVE — antes ia até 0.2 com 5
                      // linhas (perdia legibilidade). Agora cai só até 0.5
                      // mesmo com 8 linhas, e a ÚLTIMA volta a 1 (assinatura
                      // sempre visível). Fonte menor (20px) pra caber mais.
                      const isLast = i === lines.length - 1;
                      const opacity = isLast
                        ? 1
                        : Math.max(0.5, 1 - i * 0.07);
                      return (
                        <p
                          key={i}
                          className="text-white font-bold leading-[1.25]"
                          style={{
                            fontSize: 20,
                            letterSpacing: "-0.01em",
                            opacity,
                          }}
                        >
                          {line}
                        </p>
                      );
                    });
                  })()}
                </div>
              </div>
              <div
                className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.25) 100%)",
                }}
              />
            </div>
          )}

          {/* ─── Galeria ─────────────────────────────────────────────────── */}
          {data.photoUrls.length > 0 && (
            <div className="mt-5">
              <div className="flex items-baseline justify-between mb-3">
                <p
                  className="text-white font-extrabold"
                  style={{ fontSize: 18, letterSpacing: "-0.01em" }}
                >
                  Galeria
                </p>
                <button
                  type="button"
                  className="text-[12px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                >
                  Ver tudo
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {data.photoUrls.slice(0, 9).map((url, i) => (
                  <div
                    key={i}
                    className="aspect-square overflow-hidden"
                    style={{ borderRadius: 4 }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {data.photoUrls.length > 9 && (
                  <div
                    className="aspect-square flex items-center justify-center"
                    style={{
                      borderRadius: 4,
                      background: "rgba(255,255,255,0.06)",
                    }}
                  >
                    <span className="text-white font-bold text-sm">
                      +{data.photoUrls.length - 9}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Seções extras (experiências) ────────────────────────────── */}
          {data.extraSections && (
            <div className="mt-5 flex flex-col gap-3">
              <p
                className="text-white font-extrabold"
                style={{ fontSize: 18, letterSpacing: "-0.01em" }}
              >
                Mais para vocês
              </p>
              {data.extraSections}
            </div>
          )}

          <div className="h-10" />
        </div>
      </div>
    </div>
  );
}
